import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, markSession, hasSessionHint, setMemoryToken, clearMemoryToken } from '../services/api';
import i18n, { syncUserLanguage, SUPPORTED_LANGUAGES } from '../i18n';
import { initPurchases, logOutPurchases } from '../services/revenuecat';

const AuthContext = createContext(null);

/**
 * Strip credentials before serialising to localStorage. The backend still
 * returns `access_token` + `token_type` in the login response body for
 * backward compat with external clients (mobile SDKs, pytest), but the SPA
 * must NEVER write these to localStorage — that would defeat the whole
 * point of the httpOnly-cookie migration (iter 93).
 */
const safeUser = (data) => {
  if (!data || typeof data !== 'object') return data;
  const { access_token: _at, token_type: _tt, ...rest } = data;
  return rest;
};

/** The account's language picks the language of its emails and
 *  notifications. An account without one yet (older ones, Google sign-ups)
 *  takes the language the app is shown in. */
const syncLanguage = (data) => {
  if (data?.language) { syncUserLanguage(data.language); return; }
  const lang = (i18n.language || '').split('-')[0];
  if (data?.user_id && SUPPORTED_LANGUAGES.some((l) => l.code === lang)) {
    authApi.updateProfile({ language: lang }).catch(() => { /* next time */ });
  }
};

const persistUserCache = (data) => {
  try { localStorage.setItem('tofbox_user_cache', JSON.stringify(safeUser(data))); }
  catch { /* quota / private mode */ }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Fast path: no session hint AND no cached user → skip the /me round-trip
    // for first-time visitors. The hint is stored in plain localStorage so
    // even if XSS reads it, it leaks nothing (the actual token stays in an
    // httpOnly cookie that JS can't touch).
    let cachedUser = null;
    try {
      const raw = localStorage.getItem('tofbox_user_cache');
      if (raw) cachedUser = JSON.parse(raw);
    } catch { /* ignore */ }
    if (!hasSessionHint() && !cachedUser) {
      setUser(false);
      setLoading(false);
      return;
    }

    try {
      // `/auth/me` returns the profile when the httpOnly cookie is valid.
      const { data } = await authApi.me();
      setUser(data);
      syncLanguage(data);
      persistUserCache(data);
      markSession(true);
      initPurchases(data?.user_id);
    } catch (err) {
      const status = err?.response?.status;
      // Only clear the session on explicit auth failures (401/403). Any other
      // error (network down, 5xx) is treated as "still signed in, just offline".
      if (status === 401 || status === 403) {
        markSession(false);
        setUser(false);
        try { localStorage.removeItem('tofbox_user_cache'); } catch { /* noop */ }
      } else if (cachedUser) {
        setUser(cachedUser);
      } else {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await authApi.login(email, password);
    // No client-side token persisted to disk. Cookie is primary; memory
    // token is a Bearer fallback for browsers that dropped the cookie.
    setMemoryToken(data.access_token);
    markSession(true);
    setUser(data);
    syncLanguage(data);
    persistUserCache(data);
    initPurchases(data?.user_id);
    return data;
  };

  const register = async (formData) => {
    const { data } = await authApi.register(formData);
    setMemoryToken(data.access_token);
    markSession(true);
    setUser(data);
    persistUserCache(data);
    initPurchases(data?.user_id);
    return data;
  };

  const logout = async () => {
    try { await authApi.logout(); } catch { /* noop */ }
    clearMemoryToken();
    markSession(false);
    setUser(false);
    try { localStorage.removeItem('tofbox_user_cache'); } catch { /* noop */ }
    logOutPurchases();
    window.location.href = '/login';
  };

  const handleGoogleCallback = async (sessionId) => {
    // A referral link visited before choosing Google sign-in (/register?ref=,
    // /join) left its code here; the server only uses it for a new account.
    let referralCode = null;
    try { referralCode = localStorage.getItem('tofbox_ref_code'); } catch { /* private mode */ }
    const { data } = await authApi.google(sessionId, referralCode);
    try { localStorage.removeItem('tofbox_ref_code'); } catch { /* private mode */ }
    setMemoryToken(data.access_token);
    markSession(true);
    setUser(data);
    syncLanguage(data);
    persistUserCache(data);
    initPurchases(data?.user_id);
    return data;
  };

  const updateUser = (updates) => {
    if (updates?.language) syncUserLanguage(updates.language);
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, handleGoogleCallback, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
